import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Alert, Box, Chip, MenuItem, Select, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { Button } from '@verantos-dev/shared-components';

import { useExtractionPromptSection } from './useExtractionPromptSection';
import { ViewPromptDialog } from './ViewPromptDialog/ViewPromptDialog';
import type { AccuracyPromptOption } from '../../types/response';
import { getErrorMessage } from '../../utils/extractErrorMessage';
import { SectionShell } from '../SectionShell/SectionShell';

interface ExtractionPromptSectionProps {
  prompts: AccuracyPromptOption[];
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  isModelSelected: boolean;
  projectId: string | null;
  selectedPromptPath: string | null;
  isComplete: boolean;
  onSelectPrompt: (promptPath: string) => void;
  // Branch picker — rendered only when cp_pick_ai_prompt_branch is on.
  // TODO: cleanup with cp_pick_ai_prompt_branch ff
  isBranchPickerOn: boolean;
  branches?: string[];
  isBranchesLoading?: boolean;
  isBranchesError?: boolean;
  branchesError?: unknown;
  selectedBranch?: string;
  onSelectBranch?: (branch: string) => void;
}

export const ExtractionPromptSection = ({
  prompts,
  isLoading,
  isError,
  error,
  isModelSelected,
  projectId,
  selectedPromptPath,
  isComplete,
  onSelectPrompt,
  isBranchPickerOn,
  branches = [],
  isBranchesLoading = false,
  isBranchesError = false,
  branchesError,
  selectedBranch,
  onSelectBranch,
}: ExtractionPromptSectionProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const serverErrorMessage = getErrorMessage(error);
  const branchServerErrorMessage = getErrorMessage(branchesError);
  const {
    isDialogOpen,
    openDialog,
    closeDialog,
    promptBody,
    isPromptBodyLoading,
    promptBodyError,
  } = useExtractionPromptSection({
    projectId,
    promptPath: selectedPromptPath,
    branch: isBranchPickerOn ? selectedBranch : undefined,
  });

  const selectedPrompt = prompts.find((p) => p.promptPath === selectedPromptPath);

  return (
    <SectionShell
      stepNumber={3}
      title={formatMessage({ id: 'adminProjects.generateAccuracy.prompt.title' })}
      description={formatMessage({ id: 'adminProjects.generateAccuracy.prompt.description' })}
      isComplete={isComplete}
    >
      {isBranchPickerOn && (
        <Box sx={{ mb: theme.spacing(3) }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: theme.spacing(1) }}>
            {formatMessage({ id: 'adminProjects.generateAccuracy.branch.label' })}
          </Typography>
          {isBranchesError ? (
            <Alert severity="error">
              {branchServerErrorMessage ||
                formatMessage({ id: 'adminProjects.generateAccuracy.branch.loadError' })}
            </Alert>
          ) : (
            <Select
              sx={{ flex: 1 }}
              fullWidth
              displayEmpty
              disabled={!isModelSelected || isBranchesLoading}
              value={selectedBranch ?? ''}
              onChange={(e) => onSelectBranch?.(e.target.value)}
              renderValue={(value) => {
                if (!value) {
                  return (
                    <Typography color="text.secondary">
                      {formatMessage({ id: 'adminProjects.generateAccuracy.branch.placeholder' })}
                    </Typography>
                  );
                }
                return value;
              }}
            >
              {branches.map((branch) => (
                <MenuItem key={branch} value={branch}>
                  {branch}
                </MenuItem>
              ))}
            </Select>
          )}
        </Box>
      )}

      {isError ? (
        <Alert severity="error">
          {serverErrorMessage ||
            formatMessage({ id: 'adminProjects.generateAccuracy.prompt.loadError' })}
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', gap: theme.spacing(3), alignItems: 'stretch' }}>
          <Select
            sx={{ flex: 1 }}
            fullWidth
            displayEmpty
            disabled={!isModelSelected || isLoading}
            value={selectedPromptPath ?? ''}
            onChange={(e) => onSelectPrompt(e.target.value)}
            renderValue={(value) => {
              if (!value) {
                return (
                  <Typography color="text.secondary">
                    {formatMessage({
                      id: isModelSelected
                        ? 'adminProjects.generateAccuracy.prompt.placeholder'
                        : 'adminProjects.generateAccuracy.prompt.selectModelFirst',
                    })}
                  </Typography>
                );
              }
              return selectedPrompt?.promptName ?? value;
            }}
          >
            {prompts.map((prompt) => (
              <MenuItem key={prompt.promptPath} value={prompt.promptPath}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>{prompt.promptName}</Typography>
                  {prompt.isRecommended && (
                    <Chip
                      size="small"
                      label={formatMessage({ id: 'adminProjects.generateAccuracy.recommended' })}
                      sx={{
                        backgroundColor: theme.palette.info.light,
                        color: theme.palette.info.dark,
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>

          {selectedPromptPath && (
            <Box sx={{ alignSelf: 'stretch' }}>
              <Button
                id="view-prompt-button"
                variant="outlined"
                startIcon={<VisibilityOutlinedIcon />}
                displayLabel={formatMessage({
                  id: 'adminProjects.generateAccuracy.prompt.viewPrompt',
                })}
                onClickHandler={openDialog}
                sx={{
                  height: '100%',
                  width: '100%',
                }}
              />
            </Box>
          )}
        </Box>
      )}

      {selectedPromptPath && (
        <ViewPromptDialog
          open={isDialogOpen}
          onClose={closeDialog}
          promptName={selectedPrompt?.promptName ?? selectedPromptPath}
          branch={selectedBranch}
          commitSha={promptBody?.commitSha ?? null}
          body={promptBody?.body ?? null}
          isLoading={isPromptBodyLoading}
          error={promptBodyError}
        />
      )}
    </SectionShell>
  );
};
